import axios from 'axios'
import type {
  DumperHourmeter,
  CreateDumperHourmeterPayload,
  UpdateDumperHourmeterPayload,
  DumperHourmetersListQuery,
  SignInPersonPayload,
  SignOtpPayload,
  RejectDumperPayload,
  QrResolveResponse,
} from '../types/dumper'

const API_BASE_URL = (import.meta as any).env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined'
    ? window.location.hostname === 'localhost'
      ? 'http://localhost:2000'
      : window.location.hostname.includes('stg-')
        ? 'https://stg-retri-b90a7a43a76f.herokuapp.com'
        : 'https://retriapp.herokuapp.com'
    : 'http://localhost:2000')

const PLATFORM_PREFIX = '/api/v2/platform'

const platformApi = axios.create({
  baseURL: `${API_BASE_URL}${PLATFORM_PREFIX}`,
  headers: { 'Content-Type': 'application/json' },
})

platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

platformApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('operator')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const dumperHourmeterService = {
  list: async (q: DumperHourmetersListQuery = {}): Promise<DumperHourmeter[]> => {
    const { data } = await platformApi.get('/dumper-hourmeters', { params: q })
    return data
  },
  findOne: async (id: number): Promise<DumperHourmeter> => {
    const { data } = await platformApi.get(`/dumper-hourmeters/${id}`)
    return data
  },
  create: async (payload: CreateDumperHourmeterPayload): Promise<DumperHourmeter> => {
    const { data } = await platformApi.post('/dumper-hourmeters', payload)
    return data
  },
  update: async (id: number, payload: UpdateDumperHourmeterPayload): Promise<DumperHourmeter> => {
    const { data } = await platformApi.patch(`/dumper-hourmeters/${id}`, payload)
    return data
  },
  softDelete: async (id: number): Promise<DumperHourmeter> => {
    const { data } = await platformApi.delete(`/dumper-hourmeters/${id}`, { params: { mode: 'soft' } })
    return data
  },
  signInPerson: async (id: number, payload: SignInPersonPayload): Promise<DumperHourmeter> => {
    const { data } = await platformApi.post(`/dumper-hourmeters/${id}/sign-in-person`, payload)
    return data
  },
  signOtp: async (id: number, payload: SignOtpPayload): Promise<DumperHourmeter> => {
    const { data } = await platformApi.post(`/dumper-hourmeters/${id}/sign-otp`, payload)
    return data
  },
  reject: async (id: number, payload: RejectDumperPayload): Promise<DumperHourmeter> => {
    const { data } = await platformApi.post(`/dumper-hourmeters/${id}/reject`, payload)
    return data
  },
  resend: async (id: number): Promise<DumperHourmeter> => {
    const { data } = await platformApi.post(`/dumper-hourmeters/${id}/resend`)
    return data
  },
  getZones: async (params: { dealId?: number | null; operationId?: number | null }): Promise<{ loading: string[]; unloading: string[] }> => {
    const qs: Record<string, number> = {}
    if (params.dealId) qs.dealId = params.dealId
    else if (params.operationId) qs.operationId = params.operationId
    const { data } = await platformApi.get('/dumper-hourmeters/zones', { params: qs })
    return data || { loading: [], unloading: [] }
  },
  getRemisionData: async (id: number): Promise<RemisionData> => {
    const { data } = await platformApi.get(`/dumper-hourmeters/${id}/remision-data`)
    return data
  },
  downloadActa: async (id: number): Promise<void> => {
    const res = await platformApi.post(
      '/remision-volqueta/pdf',
      { dumperHourmeterId: id },
      { params: { raw: 'true' }, responseType: 'blob' }
    )
    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `remision-volqueta-${id}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },
}

export interface RemisionData {
  numero: string
  ciudad: string
  dia: string
  mes: string
  anio: string
  obra: string
  nombreAutorizado: string
  placa: string
  capacidadM3: string
  sitioCargue: string
  sitioDescargue: string
  tipoMaterial: string
  razonSocial: string
  conCargoA: string
  horaRegistro: string
  tripCount: number
  distanciaKm: string
  realM3: string
  nombreTransportador: string
  firmaTransportador: string | null
  nombreResidente: string
  observaciones: string
  fotoAdjunta: string | null
}

export interface QrGeneratePayload {
  kind: 'machinery' | 'subcontracted'
  id: number
}

export interface QrGenerateResponse {
  code: string
  url: string
  kind: string
  machineId: number
}

export const uploadService = {
  uploadFile: async (file: File): Promise<{ location: string }> => {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await platformApi.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return { location: data.location }
  },
}

export const qrService = {
  resolve: async (code: string): Promise<QrResolveResponse> => {
    const { data } = await platformApi.get(`/qr/${encodeURIComponent(code)}`)
    return data
  },
  generate: async (payload: QrGeneratePayload): Promise<QrGenerateResponse> => {
    const { data } = await platformApi.post('/qr/generate', payload)
    return data
  },
  regenerate: async (code: string): Promise<QrGenerateResponse> => {
    const { data } = await platformApi.post('/qr/regenerate', { code })
    return data
  },
  // La URL va en un <img src>, donde no se pueden poner cabeceras. Antes se le
  // pegaba `&token=` con el token de sesión: el backend NUNCA lo lee (la ruta
  // /qr/:code/image es pública) y a cambio dejaba un token de 30 días en el
  // historial del navegador, en los logs del router y en el `Referer`.
  imageUrl: (code: string, format: 'png' | 'svg' = 'png'): string =>
    `${API_BASE_URL}${PLATFORM_PREFIX}/qr/${encodeURIComponent(code)}/image?format=${format}`,
}
